import { useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import useMemoryStore from '@/stores/useMemoryStore'
import { MemoryForm } from './MemoryForm'
import { Memory } from '@/types'

interface EditMemoryDialogProps {
  orgId: string
  memory: Memory
}

export const EditMemoryDialog = ({ orgId, memory }: EditMemoryDialogProps) => {
  const [open, setOpen] = useState(false)
  const { updateMemory, isSubmitting } = useMemoryStore()
  const { toast } = useToast()

  const handleSubmit = async (values: { content: string }) => {
    try {
      await updateMemory(orgId, memory.id, values)
      toast({
        title: 'Success',
        description: 'Memory updated successfully.',
      })
      setOpen(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update memory. Please try again.',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Memory</DialogTitle>
          <DialogDescription>
            Update the content of this memory.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <MemoryForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            defaultValues={memory}
            submitButtonText="Update Memory"
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
